import { useState } from "react";
import type { FolderTreeNode } from "../hooks/useFolders";
import { ApiError } from "../lib/api";
import { useToast } from "../context/ToastContext";
import { ConfirmDialog } from "./ConfirmDialog";

export type FolderSelection = "all" | "uncategorized" | string;

interface Props {
  tree: FolderTreeNode[];
  selection: FolderSelection;
  onSelect: (sel: FolderSelection) => void;
  totalImages: number;
  uncategorizedCount: number;
  onCreate: (name: string, parentId: string | null) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const NAV_BASE = "flex h-9 w-full items-center gap-2 rounded-lg px-2.5 text-[13px] transition-colors";
const NAV_ON = "bg-white/85 font-medium text-zinc-900 shadow-raise";
const NAV_OFF = "text-zinc-600 hover:bg-white/60 hover:text-zinc-900";

export function FolderTree({
  tree,
  selection,
  onSelect,
  totalImages,
  uncategorizedCount,
  onCreate,
  onRename,
  onDelete,
}: Props) {
  const toast = useToast();
  const [creatingUnder, setCreatingUnder] = useState<string | null | undefined>(undefined);
  const [newName, setNewName] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<FolderTreeNode | null>(null);

  const toggle = (id: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const submitCreate = async () => {
    const name = newName.trim();
    if (!name) return setCreatingUnder(undefined);
    try {
      await onCreate(name, creatingUnder ?? null);
      if (creatingUnder) setExpanded((s) => new Set(s).add(creatingUnder));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not create folder.");
    } finally {
      setNewName("");
      setCreatingUnder(undefined);
    }
  };

  const submitRename = async (id: string) => {
    const name = renameValue.trim();
    setRenamingId(null);
    if (!name) return;
    try {
      await onRename(id, name);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not rename folder.");
    }
  };

  const renderNode = (node: FolderTreeNode, depth: number) => {
    const isSelected = selection === node.id;
    const hasChildren = node.children.length > 0;
    const isOpen = expanded.has(node.id);
    return (
      <li key={node.id}>
        <div
          className={`group flex items-center rounded-lg pr-1 ${isSelected ? NAV_ON : NAV_OFF}`}
          style={{ paddingLeft: `${depth * 12}px` }}
        >
          <button
            onClick={() => hasChildren && toggle(node.id)}
            className={`grid h-9 w-5 flex-shrink-0 place-items-center rounded ${
              hasChildren ? "text-zinc-400 hover:text-zinc-700" : "invisible"
            }`}
            aria-label={isOpen ? "Collapse" : "Expand"}
          >
            <svg
              className={`h-3 w-3 transition-transform ${isOpen ? "rotate-90" : ""}`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M7 5l6 5-6 5V5z" />
            </svg>
          </button>

          {renamingId === node.id ? (
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => submitRename(node.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitRename(node.id);
                if (e.key === "Escape") setRenamingId(null);
              }}
              className="field-sm my-1 w-full"
            />
          ) : (
            <button
              onClick={() => onSelect(node.id)}
              className="flex h-9 flex-1 items-center gap-1.5 truncate text-left"
            >
              <FolderIcon />
              <span className="truncate">{node.name}</span>
              <span className="ml-auto pl-1 text-2xs tabular-nums text-zinc-500">{node.imageCount || ""}</span>
            </button>
          )}

          <div className="hidden flex-shrink-0 items-center gap-0.5 group-hover:flex">
            <IconBtn
              title="New subfolder"
              onClick={() => {
                setCreatingUnder(node.id);
                setExpanded((s) => new Set(s).add(node.id));
              }}
            >
              <path d="M10 5v10M5 10h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </IconBtn>
            <IconBtn
              title="Rename"
              onClick={() => {
                setRenamingId(node.id);
                setRenameValue(node.name);
              }}
            >
              <path
                d="M4 13.5V16h2.5l7-7L11 6.5l-7 7Z M12.5 5 15 7.5 16.4 6a1.2 1.2 0 0 0 0-1.7l-.7-.7a1.2 1.2 0 0 0-1.7 0L12.5 5Z"
                fill="currentColor"
              />
            </IconBtn>
            <IconBtn title="Delete" onClick={() => setDeleteTarget(node)}>
              <path d="M6 7h8l-.7 9H6.7L6 7Zm2-3h4v2H8V4Z" fill="currentColor" />
            </IconBtn>
          </div>
        </div>

        {creatingUnder === node.id && (
          <NewFolderInput
            depth={depth + 1}
            value={newName}
            onChange={setNewName}
            onSubmit={submitCreate}
            onCancel={() => {
              setNewName("");
              setCreatingUnder(undefined);
            }}
          />
        )}

        {hasChildren && isOpen && <ul>{node.children.map((c) => renderNode(c, depth + 1))}</ul>}
      </li>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-2 pb-2">
        <span className="text-2xs font-semibold uppercase tracking-wider text-zinc-500">Folders</span>
        <button
          onClick={() => {
            setCreatingUnder(null);
            setNewName("");
          }}
          className="grid h-7 w-7 place-items-center rounded-lg text-zinc-400 transition-colors hover:bg-white/80 hover:text-zinc-800"
          title="New folder"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20">
            <path d="M10 5v10M5 10h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <ul className="scrollable min-h-0 flex-1 space-y-px overflow-y-auto pr-1">
        <li>
          <button onClick={() => onSelect("all")} className={`${NAV_BASE} ${selection === "all" ? NAV_ON : NAV_OFF}`}>
            <svg className="h-4 w-4 flex-shrink-0 text-zinc-400" viewBox="0 0 20 20" fill="currentColor">
              <path d="M3 5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5Zm2 8 3-3.5 2 2.5 3-4 4 5H5Z" />
            </svg>
            All images
            <span className="ml-auto text-2xs tabular-nums text-zinc-500">{totalImages || ""}</span>
          </button>
        </li>
        <li>
          <button
            onClick={() => onSelect("uncategorized")}
            className={`${NAV_BASE} ${selection === "uncategorized" ? NAV_ON : NAV_OFF}`}
          >
            <FolderIcon />
            Uncategorized
            <span className="ml-auto text-2xs tabular-nums text-zinc-500">{uncategorizedCount || ""}</span>
          </button>
        </li>

        {creatingUnder === null && (
          <NewFolderInput
            depth={0}
            value={newName}
            onChange={setNewName}
            onSubmit={submitCreate}
            onCancel={() => {
              setNewName("");
              setCreatingUnder(undefined);
            }}
          />
        )}

        {tree.length > 0 && <li className="h-2" />}
        {tree.map((n) => renderNode(n, 0))}
      </ul>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete folder"
        danger
        confirmLabel="Delete"
        body={
          <span>
            Delete <span className="font-medium text-zinc-900">{deleteTarget?.name}</span>? It must be empty of images.
            Any subfolders are removed too.
          </span>
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await onDelete(deleteTarget.id);
            if (selection === deleteTarget.id) onSelect("all");
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Could not delete folder.");
          } finally {
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
}

function FolderIcon() {
  return (
    <svg className="h-4 w-4 flex-shrink-0 text-zinc-400" viewBox="0 0 20 20" fill="currentColor">
      <path d="M2 5a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5Z" />
    </svg>
  );
}

function IconBtn({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className="grid h-6 w-6 place-items-center rounded-md text-zinc-400 transition-colors hover:bg-white/90 hover:text-zinc-900"
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20">
        {children}
      </svg>
    </button>
  );
}

function NewFolderInput({
  depth,
  value,
  onChange,
  onSubmit,
  onCancel,
}: {
  depth: number;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <li style={{ paddingLeft: `${depth * 12 + 20}px` }} className="py-1 pr-1">
      <input
        autoFocus
        placeholder="Folder name"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit();
          if (e.key === "Escape") onCancel();
        }}
        className="field-sm w-full"
      />
    </li>
  );
}
